/**
 * Settings, You, Date and time: the clock, the date order and the first day of the week, the same three choices as
 * the workbench's Settings › General › Date and time and kept with the person (GET/PUT /people/v1/me/display), so a
 * change here reaches their workbench and a change there reaches here. Calm: rows in one group, no artwork.
 */

import type { ClockPref, DateOrderPref, DisplayPreferences, WeekStartPref } from '@aioe/shared';
import { dateTimeExample } from '@aioe/shared/wording/dateTime';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { applyDisplayPreferences } from '../dateTimePrefs.ts';
import { useLoad, useNow } from '../hooks.ts';
import { ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow } from './ui.tsx';

const CLOCK: Record<ClockPref, string> = { system: 'Follow this computer', '12h': '12-hour (2:30 pm)', '24h': '24-hour (14:30)' };
const DATE_ORDER: Record<DateOrderPref, string> = { system: 'Follow this computer', dmy: 'Day, month, year', mdy: 'Month, day, year', ymd: 'Year, month, day' };
const WEEK_START: Record<WeekStartPref, string> = { system: 'Follow this computer', monday: 'Monday', sunday: 'Sunday' };

export function DateTimePanel() {
  const loaded = useLoad(() => routes.myDisplay().catch(() => undefined), []);
  if (!loaded.data) return null;
  return <DateTimeForm p={loaded.data} />;
}

function DateTimeForm({ p }: { p: DisplayPreferences }) {
  const [prefs, setPrefs] = useState({ clock: p.clock, dateOrder: p.dateOrder, weekStart: p.weekStart });
  const [error, setError] = useState<string>();
  const now = useNow();
  // Each choice applies and is kept at once: there is nothing to save.
  const choose = async (next: typeof prefs) => {
    const before = prefs;
    setPrefs(next);
    applyDisplayPreferences(next);
    setError(undefined);
    try {
      await routes.setMyDisplay(next);
    } catch (e) {
      setPrefs(before);
      applyDisplayPreferences(before);
      setError((e as Error).message);
    }
  };
  return (
    <ListGroup id="date-time" description={`How dates and times are written for you here, in your workbench and on your phone. Now reads ${dateTimeExample(now)}.`}>
      <ListRow title="Clock" control={<ChoiceSelect label="Clock" value={prefs.clock} options={CLOCK} onChange={(clock) => void choose({ ...prefs, clock })} className="w-56" />} />
      <ListRow title="Date order" control={<ChoiceSelect label="Date order" value={prefs.dateOrder} options={DATE_ORDER} onChange={(dateOrder) => void choose({ ...prefs, dateOrder })} className="w-56" />} />
      <ListRow title="Week starts on" control={<ChoiceSelect label="Week starts on" value={prefs.weekStart} options={WEEK_START} onChange={(weekStart) => void choose({ ...prefs, weekStart })} className="w-56" />} />
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
